"use client"

import { useState, useCallback, useMemo } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Terminal, Copy, Check, Search, Play, BookOpen } from "lucide-react"
import { toast } from "sonner"

interface RegexPattern {
  id: string
  name: string
  pattern: string
  flags?: string
  description: string
  examples: { input: string; matches: boolean }[]
  category: string
}

const patterns: RegexPattern[] = [
  // Email & Web
  {
    id: "email",
    name: "Email Address",
    pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
    description: "Validates standard email addresses",
    examples: [
      { input: "user@example.com", matches: true },
      { input: "invalid-email", matches: false },
    ],
    category: "web",
  },
  {
    id: "url",
    name: "URL",
    pattern: "^https?:\\/\\/(?:www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b(?:[-a-zA-Z0-9()@:%_\\+.~#?&\\/=]*)$",
    description: "Validates HTTP/HTTPS URLs",
    examples: [
      { input: "https://example.com/path?q=1", matches: true },
      { input: "not-a-url", matches: false },
    ],
    category: "web",
  },
  {
    id: "domain",
    name: "Domain Name",
    pattern: "^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\\.)+[a-zA-Z]{2,}$",
    description: "Validates domain names",
    examples: [
      { input: "example.com", matches: true },
      { input: "sub.example.co.uk", matches: true },
    ],
    category: "web",
  },
  {
    id: "slug",
    name: "URL Slug",
    pattern: "^[a-z0-9]+(?:-[a-z0-9]+)*$",
    description: "URL-friendly slug format",
    examples: [
      { input: "my-blog-post", matches: true },
      { input: "Invalid Slug", matches: false },
    ],
    category: "web",
  },

  // Identifiers
  {
    id: "uuid",
    name: "UUID",
    pattern: "^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$",
    flags: "i",
    description: "Validates UUID v4 format",
    examples: [
      { input: "550e8400-e29b-41d4-a716-446655440000", matches: true },
      { input: "invalid-uuid", matches: false },
    ],
    category: "identifiers",
  },
  {
    id: "hex-color",
    name: "Hex Color",
    pattern: "^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$",
    description: "Validates hex color codes",
    examples: [
      { input: "#FF5733", matches: true },
      { input: "#abc", matches: true },
    ],
    category: "identifiers",
  },
  {
    id: "mac-address",
    name: "MAC Address",
    pattern: "^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$",
    description: "Validates MAC addresses",
    examples: [
      { input: "00:1A:2B:3C:4D:5E", matches: true },
      { input: "00-1A-2B-3C-4D-5E", matches: true },
    ],
    category: "identifiers",
  },

  // Network
  {
    id: "ipv4",
    name: "IPv4 Address",
    pattern: "^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$",
    description: "Validates IPv4 addresses",
    examples: [
      { input: "192.168.1.1", matches: true },
      { input: "256.1.1.1", matches: false },
    ],
    category: "network",
  },
  {
    id: "ipv6",
    name: "IPv6 Address",
    pattern: "^(?:[a-fA-F0-9]{1,4}:){7}[a-fA-F0-9]{1,4}$",
    description: "Validates full IPv6 addresses",
    examples: [
      { input: "2001:0db8:85a3:0000:0000:8a2e:0370:7334", matches: true },
    ],
    category: "network",
  },
  {
    id: "port",
    name: "Port Number",
    pattern: "^(?:[0-9]{1,4}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$",
    description: "Validates port numbers (0-65535)",
    examples: [
      { input: "8080", matches: true },
      { input: "65536", matches: false },
    ],
    category: "network",
  },

  // Date & Time
  {
    id: "date-iso",
    name: "ISO Date",
    pattern: "^\\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\\d|3[01])$",
    description: "ISO 8601 date format (YYYY-MM-DD)",
    examples: [
      { input: "2024-01-15", matches: true },
      { input: "2024-13-01", matches: false },
    ],
    category: "datetime",
  },
  {
    id: "time-24h",
    name: "Time (24h)",
    pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d(?::[0-5]\\d)?$",
    description: "24-hour time format",
    examples: [
      { input: "23:59", matches: true },
      { input: "14:30:00", matches: true },
    ],
    category: "datetime",
  },
  {
    id: "datetime-iso",
    name: "ISO DateTime",
    pattern: "^\\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\\d|3[01])T(?:[01]\\d|2[0-3]):[0-5]\\d:[0-5]\\d(?:\\.\\d+)?(?:Z|[+-](?:[01]\\d|2[0-3]):[0-5]\\d)?$",
    description: "Full ISO 8601 datetime",
    examples: [
      { input: "2024-01-15T10:30:00Z", matches: true },
    ],
    category: "datetime",
  },

  // Numbers
  {
    id: "integer",
    name: "Integer",
    pattern: "^-?\\d+$",
    description: "Positive or negative integers",
    examples: [
      { input: "123", matches: true },
      { input: "-456", matches: true },
    ],
    category: "numbers",
  },
  {
    id: "decimal",
    name: "Decimal Number",
    pattern: "^-?\\d*\\.?\\d+$",
    description: "Decimal numbers with optional sign",
    examples: [
      { input: "3.14", matches: true },
      { input: "-0.5", matches: true },
    ],
    category: "numbers",
  },
  {
    id: "percentage",
    name: "Percentage",
    pattern: "^(?:100(?:\\.0+)?|\\d{1,2}(?:\\.\\d+)?)%?$",
    description: "Percentage values (0-100)",
    examples: [
      { input: "75%", matches: true },
      { input: "99.5", matches: true },
    ],
    category: "numbers",
  },

  // Passwords & Security
  {
    id: "password-strong",
    name: "Strong Password",
    pattern: "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$",
    description: "Min 8 chars, uppercase, lowercase, digit, special",
    examples: [
      { input: "MyPass@123", matches: true },
      { input: "weakpass", matches: false },
    ],
    category: "security",
  },
  {
    id: "jwt",
    name: "JWT Token",
    pattern: "^[A-Za-z0-9-_]+\\.[A-Za-z0-9-_]+\\.[A-Za-z0-9-_]*$",
    description: "JSON Web Token format",
    examples: [
      { input: "eyJhbGc.eyJzdWI.signature", matches: true },
    ],
    category: "security",
  },

  // Phone Numbers
  {
    id: "phone-us",
    name: "US Phone",
    pattern: "^\\+?1?[-. ]?\\(?\\d{3}\\)?[-. ]?\\d{3}[-. ]?\\d{4}$",
    description: "US phone number formats",
    examples: [
      { input: "+1 (555) 123-4567", matches: true },
      { input: "555-123-4567", matches: true },
    ],
    category: "contact",
  },
  {
    id: "phone-intl",
    name: "International Phone",
    pattern: "^\\+(?:[0-9] ?){6,14}[0-9]$",
    description: "International phone with country code",
    examples: [
      { input: "+44 20 7946 0958", matches: true },
    ],
    category: "contact",
  },

  // Credit Cards
  {
    id: "credit-card",
    name: "Credit Card",
    pattern: "^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13}|6(?:011|5[0-9]{2})[0-9]{12})$",
    description: "Visa, Mastercard, Amex, Discover",
    examples: [
      { input: "4111111111111111", matches: true },
    ],
    category: "financial",
  },

  // Common Strings
  {
    id: "alpha",
    name: "Alpha Only",
    pattern: "^[a-zA-Z]+$",
    description: "Letters only (no spaces)",
    examples: [
      { input: "HelloWorld", matches: true },
      { input: "Hello World", matches: false },
    ],
    category: "strings",
  },
  {
    id: "alphanumeric",
    name: "Alphanumeric",
    pattern: "^[a-zA-Z0-9]+$",
    description: "Letters and numbers only",
    examples: [
      { input: "User123", matches: true },
    ],
    category: "strings",
  },
  {
    id: "username",
    name: "Username",
    pattern: "^[a-zA-Z0-9_-]{3,16}$",
    description: "3-16 chars: letters, numbers, underscore, hyphen",
    examples: [
      { input: "john_doe", matches: true },
      { input: "ab", matches: false },
    ],
    category: "strings",
  },
  {
    id: "whitespace",
    name: "Contains Whitespace",
    pattern: "\\s",
    description: "Matches any whitespace character",
    examples: [
      { input: "hello world", matches: true },
      { input: "nospaces", matches: false },
    ],
    category: "strings",
  },

  // File & Path
  {
    id: "file-extension",
    name: "File Extension",
    pattern: "\\.([a-zA-Z0-9]+)$",
    description: "Extracts file extension",
    examples: [
      { input: "document.pdf", matches: true },
    ],
    category: "files",
  },
  {
    id: "image-file",
    name: "Image File",
    pattern: "\\.(jpe?g|png|gif|webp|svg|bmp|ico)$",
    flags: "i",
    description: "Common image file extensions",
    examples: [
      { input: "photo.jpg", matches: true },
      { input: "style.css", matches: false },
    ],
    category: "files",
  },
]

const categories = [
  { id: "all", label: "All" },
  { id: "web", label: "Web" },
  { id: "identifiers", label: "IDs" },
  { id: "network", label: "Network" },
  { id: "datetime", label: "Date/Time" },
  { id: "numbers", label: "Numbers" },
  { id: "security", label: "Security" },
  { id: "contact", label: "Contact" },
  { id: "financial", label: "Financial" },
  { id: "strings", label: "Strings" },
  { id: "files", label: "Files" },
]

export function RegexLibrary() {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [testInput, setTestInput] = useState("")
  const [selectedPattern, setSelectedPattern] = useState<RegexPattern | null>(null)
  const [testResult, setTestResult] = useState<{ matches: boolean; groups?: string[] } | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  const filteredPatterns = useMemo(() => {
    return patterns.filter((p) => {
      const matchesCategory = selectedCategory === "all" || p.category === selectedCategory
      const matchesSearch =
        !searchQuery ||
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      return matchesCategory && matchesSearch
    })
  }, [selectedCategory, searchQuery])

  const copy = useCallback((text: string, key: string) => {
    navigator.clipboard.writeText(text)
    setCopied(key)
    toast.success("Copied to clipboard")
    setTimeout(() => setCopied(null), 2000)
  }, [])

  const testPattern = useCallback(() => {
    if (!selectedPattern || !testInput) return

    try {
      const regex = new RegExp(selectedPattern.pattern, selectedPattern.flags)
      const match = testInput.match(regex)

      setTestResult({
        matches: !!match,
        groups: match ? Array.from(match) : undefined,
      })
    } catch {
      toast.error("Invalid regex pattern")
    }
  }, [selectedPattern, testInput])

  const selectPattern = useCallback((pattern: RegexPattern) => {
    setSelectedPattern(pattern)
    setTestInput("")
    setTestResult(null)
  }, [])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Regex Pattern Library
          </CardTitle>
          <CardDescription>
            A collection of tested, commonly-used regular expressions. Click any pattern to test it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search patterns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Pattern List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Patterns ({filteredPatterns.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {filteredPatterns.map((pattern) => (
                <div
                  key={pattern.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedPattern?.id === pattern.id
                      ? "border-primary bg-primary/5"
                      : "hover:bg-muted/50"
                  }`}
                  onClick={() => selectPattern(pattern)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{pattern.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {pattern.category}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {pattern.description}
                      </p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation()
                        copy(pattern.pattern, pattern.id)
                      }}
                    >
                      {copied === pattern.id ? (
                        <Check className="h-3 w-3" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                  <code className="text-xs text-primary/80 block mt-2 p-2 bg-muted rounded overflow-x-auto">
                    {pattern.pattern}
                  </code>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Tester */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Terminal className="h-5 w-5" />
              Test Pattern
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedPattern ? (
              <>
                <div className="space-y-2">
                  <Label className="font-medium">{selectedPattern.name}</Label>
                  <code className="text-sm text-primary block p-3 bg-muted rounded break-all">
                    /{selectedPattern.pattern}/{selectedPattern.flags || ""}
                  </code>
                  <p className="text-sm text-muted-foreground">
                    {selectedPattern.description}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Test String</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter text to test..."
                      value={testInput}
                      onChange={(e) => {
                        setTestInput(e.target.value)
                        setTestResult(null)
                      }}
                      onKeyDown={(e) => e.key === "Enter" && testPattern()}
                      className="flex-1"
                    />
                    <Button onClick={testPattern}>
                      <Play className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {testResult !== null && (
                  <div
                    className={`p-3 rounded-lg ${
                      testResult.matches
                        ? "bg-green-500/10 text-green-600"
                        : "bg-red-500/10 text-red-600"
                    }`}
                  >
                    <div className="font-medium">
                      {testResult.matches ? "✓ Matches!" : "✗ No match"}
                    </div>
                    {testResult.groups && testResult.groups.length > 1 && (
                      <div className="mt-2 text-sm">
                        <span className="text-muted-foreground">Groups: </span>
                        {testResult.groups.slice(1).map((g, i) => (
                          <Badge key={i} variant="outline" className="mr-1">
                            {g}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Examples */}
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Examples</Label>
                  <div className="space-y-1">
                    {selectedPattern.examples.map((ex, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-2 p-2 rounded text-sm ${
                          ex.matches ? "bg-green-500/10" : "bg-red-500/10"
                        }`}
                      >
                        <span className={ex.matches ? "text-green-600" : "text-red-600"}>
                          {ex.matches ? "✓" : "✗"}
                        </span>
                        <code>{ex.input}</code>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="ml-auto h-6 text-xs"
                          onClick={() => {
                            setTestInput(ex.input)
                            setTestResult(null)
                          }}
                        >
                          Try
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                Select a pattern from the list to test it
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
